import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { FormControlLabel, MenuItem, Switch } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import DialogCustom from 'commons/DialogCustom';
import DialogLocation from 'commons/DialogLocation';
import FormInput from 'commons/Form/FormInput';
import FormSelectController from 'commons/FormHook/FormSelectController';
import Text from 'commons/Text';
import API from 'configs/API';
import useCreateLocation from 'hooks/useCreateLocation';
import { ToastTopHelper } from 'utils/utils';

import DialogArtwortInfo from '../DialogArtwortInfo';
import useStyles from './styles';
import useUpdateInventory from './useUpdateInventory';

const DialogAddInventory = ({ isShowDialog, artwork, onClose, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.account.user);
  const queryClient = useQueryClient();

  const { mutate: createLocation } = useCreateLocation();

  const [isShowDialogLocation, setIsShowDialogLocation] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [isSubmittingLocation, setIsSubmittingLocation] = useState(false);

  const { data: locations } = useQuery({
    queryKey: [API.AUTH.LOCATION],
  });

  const { data: statusOptions } = useQuery({
    queryKey: [API.ARTWORK.STATUS],
  });

  const { mutate: updateArtworkInventory } = useUpdateInventory({ tab: 3, user_uuid: user?.uuid });

  const schema = yup.object().shape({
    status: yup.string().required('This field is required'),
    inventoryNumber: yup
      .string()
      .max(50, t('max_length_characters', { number: 50 }))
      .notRequired(),
    public_certificate: yup.boolean().nullable(),
  });

  const defaultValues = {
    status: '',
    location: '',
    inventoryNumber: null,
    isPublicCertificate: false,
  };

  const formMethods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit: handleSubmitForm,
    formState: { errors },
  } = formMethods;

  const handleOpenDialogLocation = () => {
    setIsShowDialogLocation(true);
  };

  const handleCancelDialogLocation = () => {
    setIsShowDialogLocation(false);
    setLocationName('');
  };

  const handleChangeLocationName = (e) => {
    setLocationName(e.target.value);
  };

  const handleSubmitDialogLocation = () => {
    if (!locationName) return;

    setIsSubmittingLocation(true);

    createLocation(
      { user: user?.id, location: locationName },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [API.ARTWORK.EDITION] });
          queryClient.invalidateQueries({ queryKey: [API.AUTH.LOCATION] });
          setIsShowDialogLocation(false);
          setLocationName('');
        },
        onSettled: () => {
          setIsSubmittingLocation(false);
        },
      },
    );
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const onSubmitForm = async (values) => {
    updateArtworkInventory(
      {
        artworkId: artwork?.id,
        payload: {
          status: values?.status,
          inventory_code: values?.inventoryNumber,
          location: values?.location,
          is_public_certificate: values?.isPublicCertificate,
        },
      },
      {
        onSuccess: () => {
          ToastTopHelper.success(t('edit_success'));
          handleClose();
        },
      },
    );
  };

  return (
    <DialogCustom
      isShowDialog={isShowDialog}
      dialogTitle={t('add_inventory')}
      textSubmit={t('add')}
      textCancel={t('cancel')}
      onCancel={handleClose}
      onSubmit={handleSubmitForm(onSubmitForm)}
      loading={loading}
      dialogHeaderStyle={classes.dialogHeaderStyle}
      dialogContentStyle={classes.dialogContentStyle}
    >
      <form onSubmit={handleSubmitForm(onSubmitForm)}>
        <DialogArtwortInfo artwork={artwork} />

        <div className={classes.fieldGroup}>
          <FormSelectController
            name="status"
            control={control}
            options={statusOptions}
            valueKey="key"
            textDisplayKey="value"
            placeholder={t('select')}
            label={t('status')}
            isRequired
            error={!!errors?.status}
            helperText={errors?.status?.message}
          />
        </div>

        <div className={classes.fieldGroup}>
          <FormSelectController
            name="location"
            control={control}
            options={locations}
            valueKey="id"
            textDisplayKey="location"
            placeholder={t('select')}
            label={t('location')}
            error={!!errors?.location}
            helperText={errors?.location?.message}
          >
            <MenuItem
              value={null}
              onClick={handleOpenDialogLocation}
            >
              <AddIcon />
              <Text ml={10}>{t('create_new_artwork_location')}</Text>
            </MenuItem>
          </FormSelectController>
        </div>

        <div className={classes.fieldGroup}>
          <FormInput
            {...formMethods.register('inventoryNumber')}
            label={t('inventory_number')}
            placeholder={t('enter')}
            inputProps={{ maxLength: 50 }}
            error={!!errors.inventoryNumber}
            helperText={errors.inventoryNumber?.message}
          />
        </div>

        <div className={classes.toggleWrapper}>
          <Controller
            name="isPublicCertificate"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={!!field.value}
                    color="primary"
                  />
                }
                label={t('public_certificate')}
              />
            )}
          />
        </div>

        <DialogLocation
          isShowDialog={isShowDialogLocation}
          disabledButton={!locationName}
          loading={isSubmittingLocation}
          value={locationName}
          onChange={handleChangeLocationName}
          onSubmit={handleSubmitDialogLocation}
          onCancel={handleCancelDialogLocation}
        />
      </form>
    </DialogCustom>
  );
};

export default DialogAddInventory;
